export type GlassSegmentKey = 'FP' | 'IP' | 'AI' | 'PE';
export type GlassRegionSide = 'L' | 'R' | 'C';
export type GlassTreatmentRecommendationKey =
  | 'noRevascularization'
  | 'endovascularOpenSurgery'
  | 'openSurgery'
  | 'endovascular';

export interface GlassRegionDefinition {
  id: string;
  labelKey: string;
  side: GlassRegionSide;
  segment: GlassSegmentKey;
  layer: 'anterior' | 'posterior';
  path: string;
  labelX: number;
  labelY: number;
}

export const GLASS_FP_IP_STAGE_MATRIX: number[][] = [
  [1, 1, 1, 2, 3],
  [1, 1, 1, 2, 3],
  [1, 2, 2, 2, 3],
  [2, 2, 2, 3, 3],
  [3, 3, 3, 3, 3],
];

export const GLASS_TREATMENT_MATRIX: Record<
  number,
  Record<number, GlassTreatmentRecommendationKey>
> = {
  1: {
    1: 'noRevascularization',
    2: 'endovascularOpenSurgery',
    3: 'openSurgery',
    4: 'openSurgery',
  },
  2: {
    1: 'noRevascularization',
    2: 'endovascular',
    3: 'endovascularOpenSurgery',
    4: 'endovascularOpenSurgery',
  },
  3: {
    1: 'noRevascularization',
    2: 'endovascular',
    3: 'endovascular',
    4: 'endovascular',
  },
};

export const GLASS_REGIONS: GlassRegionDefinition[] = [
  {
    id: 'aorta',
    labelKey: 'aorta',
    side: 'C',
    segment: 'AI',
    layer: 'anterior',
    path: 'M 281 300 C 281 320 280 345 280 370 C 280 385 280 392 280 398',
    labelX: 312,
    labelY: 340,
  },
  {
    id: 'ci_l',
    labelKey: 'commonIliac',
    side: 'L',
    segment: 'AI',
    layer: 'anterior',
    path: 'M 280 398 C 268 410 254 420 240 432 C 234 437 228 442 224 448',
    labelX: 218,
    labelY: 418,
  },
  {
    id: 'ci_r',
    labelKey: 'commonIliac',
    side: 'R',
    segment: 'AI',
    layer: 'anterior',
    path: 'M 280 398 C 292 410 306 420 320 432 C 326 437 332 442 336 448',
    labelX: 344,
    labelY: 418,
  },
  {
    id: 'ei_l',
    labelKey: 'externalIliac',
    side: 'L',
    segment: 'AI',
    layer: 'anterior',
    path: 'M 224 448 C 222 462 220 476 222 492',
    labelX: 198,
    labelY: 472,
  },
  {
    id: 'ei_r',
    labelKey: 'externalIliac',
    side: 'R',
    segment: 'AI',
    layer: 'anterior',
    path: 'M 336 448 C 338 462 340 476 338 492',
    labelX: 362,
    labelY: 472,
  },
  {
    id: 'cfa_l',
    labelKey: 'commonDeepFemoral',
    side: 'L',
    segment: 'FP',
    layer: 'anterior',
    path: 'M 222 492 L 220 514 M 220 510 C 212 520 206 532 202 548',
    labelX: 188,
    labelY: 524,
  },
  {
    id: 'cfa_r',
    labelKey: 'commonDeepFemoral',
    side: 'R',
    segment: 'FP',
    layer: 'anterior',
    path: 'M 338 492 L 340 514 M 340 510 C 348 520 354 532 358 548',
    labelX: 374,
    labelY: 524,
  },
  {
    id: 'sfa_l',
    labelKey: 'sfa',
    side: 'L',
    segment: 'FP',
    layer: 'anterior',
    path: 'M 220 514 C 222 546 224 590 226 632 C 228 650 232 666 236 678',
    labelX: 194,
    labelY: 594,
  },
  {
    id: 'sfa_r',
    labelKey: 'sfa',
    side: 'R',
    segment: 'FP',
    layer: 'anterior',
    path: 'M 340 514 C 338 546 336 590 334 632 C 332 650 328 666 324 678',
    labelX: 368,
    labelY: 594,
  },
  {
    id: 'pop_l',
    labelKey: 'popliteal',
    side: 'L',
    segment: 'FP',
    layer: 'posterior',
    path: 'M 236 678 C 238 692 240 706 244 720',
    labelX: 200,
    labelY: 702,
  },
  {
    id: 'pop_r',
    labelKey: 'popliteal',
    side: 'R',
    segment: 'FP',
    layer: 'posterior',
    path: 'M 324 678 C 322 692 320 706 316 720',
    labelX: 362,
    labelY: 702,
  },
  {
    id: 'tpt_l',
    labelKey: 'tibioperonealTrunk',
    side: 'L',
    segment: 'IP',
    layer: 'anterior',
    path: 'M 244 720 L 244 738',
    labelX: 208,
    labelY: 730,
  },
  {
    id: 'tpt_r',
    labelKey: 'tibioperonealTrunk',
    side: 'R',
    segment: 'IP',
    layer: 'anterior',
    path: 'M 316 720 L 316 738',
    labelX: 354,
    labelY: 730,
  },
  {
    id: 'at_l',
    labelKey: 'anteriorTibial',
    side: 'L',
    segment: 'IP',
    layer: 'anterior',
    path: 'M 244 738 C 238 758 230 790 224 818 C 220 838 220 854 222 864',
    labelX: 198,
    labelY: 798,
  },
  {
    id: 'at_r',
    labelKey: 'anteriorTibial',
    side: 'R',
    segment: 'IP',
    layer: 'anterior',
    path: 'M 316 738 C 322 758 330 790 336 818 C 340 838 340 854 338 864',
    labelX: 364,
    labelY: 798,
  },
  {
    id: 'pt_l',
    labelKey: 'posteriorTibial',
    side: 'L',
    segment: 'IP',
    layer: 'posterior',
    path: 'M 244 738 C 248 770 252 808 250 856',
    labelX: 262,
    labelY: 800,
  },
  {
    id: 'pt_r',
    labelKey: 'posteriorTibial',
    side: 'R',
    segment: 'IP',
    layer: 'posterior',
    path: 'M 316 738 C 312 770 308 808 310 856',
    labelX: 298,
    labelY: 800,
  },
  {
    id: 'pedal_l',
    labelKey: 'pedalArch',
    side: 'L',
    segment: 'PE',
    layer: 'anterior',
    path:
      'M 222 864 C 215 872 208 880 200 884 M 250 856 C 244 870 232 880 220 884',
    labelX: 188,
    labelY: 876,
  },
  {
    id: 'pedal_r',
    labelKey: 'pedalArch',
    side: 'R',
    segment: 'PE',
    layer: 'anterior',
    path:
      'M 338 864 C 345 872 352 880 360 884 M 310 856 C 316 870 328 880 340 884',
    labelX: 372,
    labelY: 876,
  },
];

export function getGlassStage(
  fpGrade: number | null | undefined,
  ipGrade: number | null | undefined,
): number | null {
  if (fpGrade == null || ipGrade == null) {
    return null;
  }

  return GLASS_FP_IP_STAGE_MATRIX[fpGrade]?.[ipGrade] ?? null;
}

export function getGlassTreatmentRecommendation(
  glassStage: number | null | undefined,
  wifiStage: number | null | undefined,
): GlassTreatmentRecommendationKey | null {
  if (glassStage == null || wifiStage == null) {
    return null;
  }

  return GLASS_TREATMENT_MATRIX[glassStage]?.[wifiStage] ?? null;
}
